/**
 * ที่เก็บ session token ฝั่ง client — `localStorage` และ **เก็บเพียงสิ่งเดียวนั้น** (D-049)
 *
 * D-049 เลือก `localStorage` เพราะ `POST /v1/auth/login` คืน bearer token ไม่ใช่
 * httpOnly cookie จึงต้องเก็บฝั่ง client; `sessionStorage` หายเมื่อปิดแท็บซึ่งขัดกับ
 * session 16 ชั่วโมงของ handheld (D-046) และ in-memory ล้วนหายทุกครั้งที่ refresh
 * ราคาที่จ่ายคือ XSS ที่อ่าน token ได้ ซึ่งถูกลดด้วยการที่แอปไม่โหลด script จาก
 * origin ภายนอกเลย — ข้อนั้นจึงต้องเป็นจริงตลอดไป ไม่ใช่แค่วันที่เขียนไฟล์นี้
 *
 * **คีย์เดียว ค่าเดียว**: ห้ามเพิ่มคีย์อื่นในไฟล์นี้เด็ดขาด โดยเฉพาะ permission set
 * หรือข้อมูลธุรกิจ — permission set ที่ค้างในเบราว์เซอร์คือ allow-list ที่ยังทำงาน
 * ต่อหลังผู้ดูแลระบบถอนสิทธิ์ไปแล้ว ซึ่งเป็นความล้มเหลวที่ D-049 เขียนไว้เพื่อกัน
 *
 * ทุกการแตะ storage ถูกห่อด้วย `try/catch` ทั้งหมด: private window, storage ที่ถูก
 * นโยบายเบราว์เซอร์ปิด และ quota error ต้องทำให้แอป "ถือว่ายังไม่ได้เข้าสู่ระบบ"
 * ไม่ใช่ทำให้แอปพัง — token ที่อ่านไม่ได้ไม่ใช่ token ที่ใช้ได้
 */

/** คีย์เดียวที่แอปนี้เขียนลง `localStorage` */
export const TOKEN_STORAGE_KEY = 'cap-erp.session-token';

/**
 * อ่าน `localStorage` แบบที่ยอมรับได้ว่ามันอาจไม่มีอยู่
 *
 * การ *เข้าถึง* property เองก็ throw ได้ในเบราว์เซอร์ที่ปิด storage ไว้ (ไม่ใช่แค่
 * ตอน `getItem`) จึงต้องอยู่ใน `try` ตั้งแต่บรรทัดแรก
 */
function openStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // storage ถูกปิด/ถูกบล็อก — ถือว่าไม่มีที่เก็บ ไม่ใช่ข้อผิดพลาดที่ต้องโยนต่อ
    return null;
  }
}

/** token ที่เก็บไว้ หรือ `null` ถ้าไม่มี/อ่านไม่ได้/เป็นค่าว่าง */
export function readStoredToken(): string | null {
  const storage = openStorage();
  if (storage === null) {
    return null;
  }

  let raw: string | null;
  try {
    raw = storage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // อ่านไม่ได้ = ไม่มี token ที่ใช้ได้ ไม่ใช่ "มี token แต่ยังไม่รู้ค่า"
    return null;
  }

  if (typeof raw !== 'string') {
    return null;
  }
  const token = raw.trim();
  return token === '' ? null : token;
}

/**
 * เขียน token ลง storage — คืน `false` เมื่อเขียนไม่ได้ (quota/โหมดส่วนตัว)
 *
 * ผู้เรียกต้องใช้ค่าที่คืนเพื่อบอกผู้ใช้ตามจริงว่าเซสชันนี้จะไม่รอดการรีเฟรช
 * ไม่ใช่กลืนความล้มเหลวแล้วปล่อยให้ผู้ใช้เจอหน้า login ซ้ำโดยไม่รู้สาเหตุ
 */
export function writeStoredToken(token: string): boolean {
  const storage = openStorage();
  if (storage === null) {
    return false;
  }
  try {
    storage.setItem(TOKEN_STORAGE_KEY, token);
    return true;
  } catch {
    return false;
  }
}

/** ลบ token ทิ้ง — ใช้ทั้งตอนกดออกจากระบบและตอนเจอ `401` จาก endpoint ใดก็ตาม */
export function clearStoredToken(): void {
  const storage = openStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // storage ที่เขียนไม่ได้ก็ลบไม่ได้ และไม่มีอะไรให้กู้ — สถานะในหน่วยความจำ
    // ของ `session.ts` ยังถูกล้างอยู่ดี ผู้ใช้จึงออกจากระบบสำเร็จเสมอ
  }
}
